import { Injectable } from "@angular/core";
import * as m from "moment-timezone";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { FeatureFlagService } from "./feature-flag.service";
import { FeatureFlagKeys } from "./feature-flag.models";
import { NovaInsightSettingsService } from "./nova-insight-settings.service";
import { UserService } from "../authentication/user.service";
import { SettingsLocalService } from "../pages/settings/settings.service";

// Namespaced per user, not a single shared key. Dismissal is a per-person decision, but localStorage
// is per-BROWSER: on a shared machine - a clinic, a demo laptop, anyone testing two accounts - one
// user's dismissal silenced the widget for whoever logged in next, for the rest of the local day,
// with no error and nothing on screen to explain the absence. The failure was invisible in exactly
// the case that produces it, since the second user has no way to know a first one dismissed anything.
const DISMISSED_DATE_KEY_PREFIX = "novaInsightDismissedDate";
// How often to re-check eligibility (today's local notification time passed) and whether the
// dismissed-for-today flag has rolled over into a new day, so a session left open across either
// boundary picks the widget back up without a page reload - loose enough not to be wasteful,
// tight enough that "appears at the configured time" doesn't mean "appears up to an hour late".
const RECHECK_INTERVAL_MS = 60000;

/**
 * Today's date in the user's own timezone, as YYYY-MM-DD. Deliberately not
 * `toISOString().slice(0, 10)`, which is the *UTC* date: on UTC+10 that rolls over at 10am local,
 * so a "dismissed for today" flag keyed to it silenced the widget from 10am one day until 10am
 * the next - straddling exactly the morning notification times it was meant to fire at.
 */
function localDateString(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Owns whether the Nova Insight widget (FLIP-169) is currently dismissed, and - per FLIP-172 -
 * whether it's eligible to show at all. As of FLIP-215 that is four questions, not two:
 * onboarding is finished, the account is past its registration day, the settings-driven on/off
 * toggle is on, and today's configured notification time has passed. Modeled on EmailConfirmationSnoozeService's shape
 * (localStorage-backed BehaviorSubject, since the widget is mounted once in app.component and
 * won't re-read storage on its own).
 *
 * Both halves of the question are decided in the user's **local** day. NotificationTime is stored
 * as UTC (see NotificationSettings.NotificationTime) but means a local wall-clock time, so
 * eligibility converts it back to local before comparing. Unlike Score/BSB there's no
 * server-side scheduled push for Nova Insight; the widget just checks this on load and
 * periodically while mounted.
 *
 * "Dismissed" is scoped to the current local date, not permanent - the setting is meant to fire
 * daily (same recurring semantics as Score/BSB's alert), so dismissing today's widget shouldn't
 * silence it forever. The stored value is a date string ("dismissed through this day"), not a
 * boolean; once the local date rolls over, the stored date no longer matches and the widget is
 * eligible to fire again next time the notification time is reached. Keying this to the *UTC*
 * date was the original bug: on UTC+10 that day ran 10am -> 10am, so one dismissal swallowed the
 * whole of the next morning.
 */
@Injectable({
  providedIn: "root",
})
export class NovaInsightVisibilityService {
  private dismissedSource = new BehaviorSubject<boolean>(this.isDismissedToday());
  dismissedChanged$ = this.dismissedSource.asObservable();

  private eligibleSource = new BehaviorSubject<boolean>(false);
  eligibleChanged$ = this.eligibleSource.asObservable();

  /** Combined "should the widget actually render" signal: not dismissed (today) AND settings-eligible. */
  showWidget$: Observable<boolean> = combineLatest([
    this.dismissedChanged$,
    this.eligibleChanged$,
  ]).pipe(map(([dismissed, eligible]) => !dismissed && eligible));

  private isEnabled = false;
  private notificationTimeUtc: string | null = null;
  // Set by show() - the /test page's "force show" trigger bypasses the real on/off + time
  // gating entirely, same as it always has, rather than only working once those happen to
  // already be satisfied.
  private forceEligible = false;

  constructor(
    private novaInsightSettingsService: NovaInsightSettingsService,
    private userService: UserService,
    private settingsLocalService: SettingsLocalService,
    private featureFlagService: FeatureFlagService
  ) {
    // Only fetch when a session already exists (a reload with a stored login). On a cold start at
    // the login screen there is no token yet, so the request would 401 and leave the widget
    // permanently ineligible for the whole session - the login below is what covers that case.
    if (this.userService.isUserLoggedIn()) {
      this.refreshSettings();
    }

    // This service is `providedIn: "root"` and constructed once at bootstrap, but logging in is an
    // in-app navigation, not a page load - nothing re-creates it. Without this, settings fetched
    // (or failed) before login would stand for the rest of the session. Mirrors how app.component
    // drives maybeLoadAuthenticatedData() off the same signal.
    this.userService.userLoginStatusChanged$.subscribe((isLoggedIn) => {
      // Re-read the dismissal too, not just the settings. `dismissedSource` is a BehaviorSubject
      // holding whoever was last logged in, and the key it answers for has just changed underneath
      // it - without this, a second user inherits the first's dismissed state until the 60s poll
      // happens to correct it, which is the whole bug this per-user key exists to prevent.
      this.recomputeDismissed();

      if (isLoggedIn) {
        this.refreshSettings();
      } else {
        this.clearSettings();
      }
    });

    setInterval(() => {
      this.recomputeDismissed();
      this.recomputeEligibility();
    }, RECHECK_INTERVAL_MS);
  }

  /**
   * Drops the previous user's settings on logout, so the next user doesn't inherit their
   * eligibility before their own settings land. Also clears the /test page's force flag.
   */
  private clearSettings(): void {
    this.isEnabled = false;
    this.notificationTimeUtc = null;
    this.forceEligible = false;
    this.recomputeEligibility();
  }

  /** Re-fetches the on/off + notification-time setting from the backend and re-evaluates eligibility. Call after saving a settings change so the widget doesn't wait for the next poll. */
  refreshSettings(): void {
    // FLIP-527: eligibility needs the nova-insights flag as well. init() is idempotent (the same promise
    // app.component's boot call gets), so this only waits; once the flags land, eligibility is re-evaluated so
    // a flag that resolves after login still brings the widget up without a reload (or keeps it down).
    this.featureFlagService.init().then(() => this.recomputeEligibility());

    this.novaInsightSettingsService.get().subscribe({
      next: (settings) => {
        this.isEnabled = settings.isEnabled;
        this.notificationTimeUtc = settings.notificationTime;
        this.recomputeEligibility();
      },
      // No settings row yet / request failed - stay ineligible rather than guessing.
      error: () => {
        this.isEnabled = false;
        this.notificationTimeUtc = null;
        this.recomputeEligibility();
      },
    });
  }

  /**
   * FLIP-527: whether Nova Insights are switched on for this user at all (the `nova-insights` flag, seeded
   * off). Synchronous and false until the flags have loaded, so nothing shows in the gap.
   */
  isFeatureEnabled(): boolean {
    return this.featureFlagService.isEnabled(FeatureFlagKeys.NovaInsights);
  }

  /**
   * The dismissal key for whoever is logged in now.
   *
   * Falls back to a shared "anon" bucket when there is no user, which is only reachable before
   * login - a state in which the widget is ineligible anyway, so nothing is written under it in
   * practice. Resolved per call rather than cached at construction: this service is
   * `providedIn: "root"` and built once at bootstrap, before anybody has logged in, so a value
   * captured then would name the wrong user for the whole session.
   */
  private dismissedKey(): string {
    const userGuid = this.userService.getUser()?.userGuid;
    return `${DISMISSED_DATE_KEY_PREFIX}_${userGuid || "anon"}`;
  }

  private isDismissedToday(): boolean {
    return localStorage.getItem(this.dismissedKey()) === localDateString();
  }

  private recomputeDismissed(): void {
    this.dismissedSource.next(this.isDismissedToday());
  }

  /**
   * FLIP-215: the two gates that come before the settings ones.
   *
   * Onboarding first - the widget was mounted globally in app.component, so it could float over
   * onboarding screens for a user who is already logged in but hasn't finished registering.
   * Gated on the onboarding flag rather than on a route list, matching OnboardingGuard and
   * app.component's showNav (and NovaCompanionFabComponent, gated the same way for FLIP-216).
   *
   * Then account age. The earliest an Insight can appear is day 2 of the account, and this
   * takes precedence over the notification-time check by design: on registration day the
   * configured time is irrelevant, because there is nothing yet for Nova to have noticed. This
   * is what stops Nova commenting on the engagement of somebody who has not used the product.
   */
  private isPastOnboardingAndDayOne(): boolean {
    return this.userService.isOnboardingComplete() && !this.userService.isRegistrationDay();
  }

  private recomputeEligibility(): void {
    // The flag outranks everything, including the /test page's force flag.
    this.eligibleSource.next(
      this.isFeatureEnabled() &&
        (this.forceEligible ||
          (this.isEnabled &&
            this.isPastOnboardingAndDayOne() &&
            this.isPastNotificationTime(this.notificationTimeUtc)))
    );
  }

  /**
   * Whether the configured notification time has been reached today, in the user's own timezone.
   *
   * `NotificationTime` is stored as UTC but *means* a local wall-clock time - the settings page
   * converts the user's "7:45am" to UTC before saving. So this converts the stored UTC
   * time-of-day back to a local time-of-day and compares minutes-of-day against now. Comparing
   * times-of-day rather than instants keeps the whole question inside one day: the previous
   * approach rebuilt the alert on *today's UTC date*, which for a morning local time (stored as
   * the previous UTC day's evening) made the widget eligible from the evening before.
   */
  private isPastNotificationTime(utcTime: string | null): boolean {
    if (!utcTime) {
      return false;
    }

    // FLIP-215: converted through the user's SAVED timezone, not the browser's offset.
    //
    // The settings page renders this same stored value through `userSettings.timezone`
    // (settings.component.loadNovaInsightSettings), while this check used
    // `Date.getTimezoneOffset()` - the zone the browser happens to be in. For anyone whose
    // browser zone differs from their saved zone, Settings displayed "10:00am" while the widget
    // fired at some other local hour, and the two were never reconciled. Reading the same
    // timezone the settings page reads is what makes the displayed time the time it fires.
    //
    // Falls back to the browser's zone rather than the settings page's literal
    // "Australia/Brisbane", because SettingsLocalService self-heals a missing timezone to
    // exactly `m.tz.guess()` and saves it - so guessing here agrees with the value about to be
    // persisted, whereas Brisbane would disagree with it for every non-Australian user.
    const timezone = this.settingsLocalService.getSettings()?.timezone || m.tz.guess();
    const today = m().tz(timezone);
    const alertLocal = m.utc(`${today.format("YYYY-MM-DD")} ${utcTime}`).tz(timezone);

    // Compared as minutes-of-day rather than as instants, so the whole question stays inside one
    // day: anchoring the alert to a date makes a morning local time (stored as the previous UTC
    // day's evening) look like it has already passed the evening before.
    const alertLocalMinutes = alertLocal.hours() * 60 + alertLocal.minutes();
    const nowLocalMinutes = today.hours() * 60 + today.minutes();

    return nowLocalMinutes >= alertLocalMinutes;
  }

  /** Dismisses the widget for the rest of today (local), for THIS user - it becomes eligible to fire again once tomorrow's notification time is reached. */
  dismiss(): void {
    localStorage.setItem(this.dismissedKey(), localDateString());
    this.dismissedSource.next(true);
  }

  /**
   * Clears today's dismissed flag and forces eligibility so the widget shows again immediately
   * regardless of the on/off toggle or notification time. Used by the /test playground to
   * re-trigger the widget on demand.
   */
  show(): void {
    localStorage.removeItem(this.dismissedKey());
    this.dismissedSource.next(false);
    this.forceEligible = true;
    this.recomputeEligibility();
  }
}
